/**
 * PhotoDeleteConfirm — small confirm dialog for photo delete.
 *
 * FT-343 (2026-07-08). See DESIGN-BRIEF.md §1.2.
 *
 * Deliberately thin — the ACTUAL delete (IDB + outbox) is executed by the
 * PhotoAttachment shell after the user confirms. This component only surfaces
 * the confirmation UI.
 *
 * Reuses `Modal` from `src/components/tailwind/common/Modal.js` — no new modal
 * component; per FT-343 hard constraint.
 */

import React, { useRef, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Modal, ModalBody, ModalActions } from '../../tailwind/common/Modal';

/**
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onCancel   Closes the dialog without deleting.
 * @param {() => (void|Promise<void>)} props.onConfirm  Executes the delete.
 * @param {string} [props.testId]
 */
export function PhotoDeleteConfirm({ isOpen, onCancel, onConfirm, testId = 'photo-delete-confirm' }) {
  const [busy, setBusy] = useState(false);
  // Ref-based double-click guard — React `busy` state doesn't flip fast enough
  // to catch a synchronous second click within the same event loop tick.
  const inFlightRef = useRef(false);

  const handleConfirm = async () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setBusy(true);
    try {
      await onConfirm?.();
    } finally {
      inFlightRef.current = false;
      setBusy(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={busy ? () => {} : onCancel} title="Delete photo?" size="sm">
      <ModalBody>
        <div className="flex items-start gap-3" data-testid={testId}>
          <div className="shrink-0 text-red-600" aria-hidden>
            <AlertTriangle className="h-6 w-6" />
          </div>
          <p className="text-sm text-gray-700">Delete this photo? This cannot be undone.</p>
        </div>
      </ModalBody>
      <ModalActions>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            data-testid={`${testId}-cancel`}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={busy}
            data-testid={`${testId}-confirm`}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded hover:bg-red-700 disabled:opacity-50"
          >
            {busy ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      </ModalActions>
    </Modal>
  );
}

export default PhotoDeleteConfirm;
